import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  createProgram,
  updateProgram,
  listProgramPlaces,
  setProgramPlaces,
  createPlace,
  type Program,
  type ProgramCreate,
  type ProgramUpdate,
  type Place,
  type HappeningType,
} from '@littlebooker/dal';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { DangerZone } from '@/components/DangerZone';

const slugify = (value: string) =>
  value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .replace(/-{2,}/g, '-');

function isoToLocalInput(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function localInputToIso(value: string): string {
  return new Date(value).toISOString();
}

type Mode = 'create' | 'edit';

export type ProgramFormDialogProps = {
  mode: Mode;
  program: Program | null;
  stages: Place[];
  onClose: () => void;
  onSaved?: (program: Program) => void;
  onDeleteRequest?: () => void;
};

export function ProgramFormDialog({ mode, program, stages, onClose, onSaved, onDeleteRequest }: ProgramFormDialogProps) {
  const queryClient = useQueryClient();

  const [name, setName] = useState(program?.name ?? '');
  const [slug, setSlug] = useState(program?.slug ?? '');
  const [slugTouched, setSlugTouched] = useState(mode === 'edit');
  const [description, setDescription] = useState(program?.description ?? '');
  const [locationInput, setLocationInput] = useState(
    ((program?.settings as { location?: string } | null)?.location) ?? '',
  );
  const [startTime, setStartTime] = useState(isoToLocalInput(program?.start_time));
  const [endTime, setEndTime] = useState(isoToLocalInput(program?.end_time));
  const [defaultType, setDefaultType] = useState<HappeningType>(
    (program?.default_happening_type as HappeningType) || 'performance'
  );
  const [selectedStageIds, setSelectedStageIds] = useState<Set<string>>(new Set());
  const [newStageName, setNewStageName] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (mode !== 'edit' || !program) return;
    let cancelled = false;
    listProgramPlaces(program.id).then((links) => {
      if (cancelled) return;
      setSelectedStageIds(new Set(links.map((l) => l.place_id)));
    });
    return () => {
      cancelled = true;
    };
  }, [mode, program]);

  useEffect(() => {
    if (slugTouched) return;
    setSlug(slugify(name));
  }, [name, slugTouched]);

  const orderedStages = useMemo(
    () => [...stages].sort((a, b) => a.name.localeCompare(b.name)),
    [stages]
  );

  const createStageMutation = useMutation({
    mutationFn: (stageName: string) =>
      createPlace({
        name: stageName,
        slug: slugify(stageName),
        type: 'stage',
        parent_id: null,
        address: null,
        emergency_contact_phone: null,
        is_active: true,
        metadata: {},
      }),
    onSuccess: (place) => {
      setSelectedStageIds((prev) => new Set(prev).add(place.id));
      setNewStageName('');
      queryClient.invalidateQueries({ queryKey: ['places', { type: 'stage' }] });
      queryClient.invalidateQueries({ queryKey: ['places'] });
    },
  });

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!startTime || !endTime) throw new Error('Start and end times are required');
      const startIso = localInputToIso(startTime);
      const endIso = localInputToIso(endTime);
      if (new Date(endIso) <= new Date(startIso)) {
        throw new Error('End time must be after start time');
      }

      const placeIds = Array.from(selectedStageIds);
      const trimmedLocation = locationInput.trim();

      if (mode === 'create') {
        const created = await createProgram({
          name: name.trim(),
          slug: slug.trim() || slugify(name),
          description: description.trim() || null,
          start_time: startIso,
          end_time: endIso,
          default_happening_type: defaultType,
          settings: trimmedLocation ? { location: trimmedLocation } : {},
        } as ProgramCreate);
        await setProgramPlaces(created.id, placeIds);
        return created;
      }

      if (!program) throw new Error('No program to edit');
      // Preserve other settings (staffing config, reveal flag, timetable token).
      const nextSettings = { ...((program.settings as Record<string, unknown>) ?? {}) };
      if (trimmedLocation) nextSettings.location = trimmedLocation;
      else delete nextSettings.location;
      const patch: ProgramUpdate = {
        name: name.trim(),
        slug: slug.trim() || slugify(name),
        description: description.trim() || null,
        start_time: startIso,
        end_time: endIso,
        default_happening_type: defaultType,
        settings: nextSettings,
      };
      const updated = await updateProgram(program.id, patch);
      await setProgramPlaces(program.id, placeIds);
      return updated;
    },
    onSuccess: (saved) => {
      queryClient.invalidateQueries({ queryKey: ['programs'] });
      queryClient.invalidateQueries({ queryKey: ['program-place-counts'] });
      queryClient.invalidateQueries({ queryKey: ['program', saved.id] });
      queryClient.invalidateQueries({ queryKey: ['program-places', saved.id] });
      onSaved?.(saved);
      onClose();
    },
    onError: (err) => {
      setError(err instanceof Error ? err.message : 'Failed to save program');
    },
  });

  const toggleStage = (id: string) => {
    setSelectedStageIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleAddStage = () => {
    const trimmed = newStageName.trim();
    if (!trimmed) return;
    createStageMutation.mutate(trimmed);
  };

  const formValid = name.trim() && slug.trim() && startTime && endTime;

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{mode === 'create' ? 'New program' : 'Edit program'}</DialogTitle>
          <DialogDescription>
            A program groups performances across multiple stages over a date range.
          </DialogDescription>
        </DialogHeader>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            setError(null);
            saveMutation.mutate();
          }}
        >
          <div className="space-y-4 px-4 sm:px-6 py-4">
          <div>
            <label className="text-sm font-medium">Name</label>
            <input
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2"
              placeholder="Acme Summer 2026"
            />
          </div>

          <div>
            <label className="text-sm font-medium">Slug</label>
            <input
              required
              value={slug}
              onChange={(e) => {
                setSlugTouched(true);
                setSlug(e.target.value);
              }}
              className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2"
              placeholder="acme-summer-2026"
            />
            <p className="mt-1 text-xs text-muted-foreground">
              Auto-generated from the name. Must be unique within your account.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className="text-sm font-medium">Start</label>
              <input
                type="datetime-local"
                required
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
                className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2"
              />
            </div>
            <div>
              <label className="text-sm font-medium">End</label>
              <input
                type="datetime-local"
                required
                value={endTime}
                onChange={(e) => setEndTime(e.target.value)}
                className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2"
              />
            </div>
          </div>

          <div>
            <label className="text-sm font-medium">Default performance type</label>
            <select
              value={defaultType}
              onChange={(e) => setDefaultType(e.target.value as HappeningType)}
              className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2"
            >
              <option value="performance">Performance (festival/club)</option>
              <option value="tech">Tech / Internal (soundcheck, changeover)</option>
              <option value="show">Radio show</option>
              <option value="event">Event</option>
              <option value="recording">Recording</option>
              <option value="festival">Festival</option>
              <option value="mix">Mix</option>
            </select>
            <p className="mt-1 text-xs text-muted-foreground">
              New happenings created from this program's timetable will default to this type.
            </p>
          </div>

          <div>
            <label className="text-sm font-medium">Location</label>
            <input
              value={locationInput}
              onChange={(e) => setLocationInput(e.target.value)}
              className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2"
              placeholder="e.g. Parc de la Nature, Weesp"
            />
            <p className="mt-1 text-xs text-muted-foreground">
              Shown in the header of the staff timetable pages.
            </p>
          </div>

          <div>
            <label className="text-sm font-medium">Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2"
              placeholder="Optional"
            />
          </div>

          <div className="space-y-2 rounded-md border border-input p-3">
            <p className="text-sm font-medium">Stages</p>
            <p className="text-xs text-muted-foreground">
              Pick the stages used by this program. New performances scheduled inside the
              program will be limited to these stages.
            </p>

            <div className="max-h-48 space-y-1 overflow-y-auto rounded-md bg-muted/30 p-2">
              {orderedStages.length === 0 ? (
                <p className="text-xs text-muted-foreground">
                  No stages exist yet. Create one below.
                </p>
              ) : (
                orderedStages.map((stage) => (
                  <label
                    key={stage.id}
                    className="flex cursor-pointer items-center gap-2 rounded px-2 py-1 hover:bg-muted"
                  >
                    <input
                      type="checkbox"
                      checked={selectedStageIds.has(stage.id)}
                      onChange={() => toggleStage(stage.id)}
                      className="h-4 w-4 rounded border-input"
                    />
                    <span className="text-sm">{stage.name}</span>
                    <span className="text-xs text-muted-foreground">{stage.slug}</span>
                  </label>
                ))
              )}
            </div>

            <div className="flex items-center gap-2 pt-2">
              <input
                value={newStageName}
                onChange={(e) => setNewStageName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddStage();
                  }
                }}
                placeholder="New stage name"
                className="flex-1 rounded-md border border-input bg-background px-3 py-1.5 text-sm"
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleAddStage}
                disabled={!newStageName.trim() || createStageMutation.isPending}
              >
                {createStageMutation.isPending ? 'Adding…' : 'Add stage'}
              </Button>
            </div>
            {createStageMutation.isError && (
              <p className="text-xs text-destructive">
                Failed to create stage. It might already exist with that slug.
              </p>
            )}
          </div>

          {error && <p className="text-sm text-destructive">{error}</p>}

          {mode === 'edit' && onDeleteRequest && (
            <DangerZone
              entityLabel="program"
              onDelete={onDeleteRequest}
            />
          )}
          </div>

          <DialogFooter className="gap-2 sm:justify-end">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={!formValid || saveMutation.isPending}>
              {saveMutation.isPending
                ? 'Saving…'
                : mode === 'create'
                  ? 'Create program'
                  : 'Save changes'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
